import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronUp, Image as ImageIcon, Megaphone, Pencil, Plus, Trash2, Upload, X,
} from '../../icons';
import {
  Badge, Button, Chip, EmptyState, FormField, Input, ListPane, ListPaneItem,
  Modal, ModalActions, ModalFootNote, Select, Switch, Table, Tabs,
} from '../../components/shared';
import {
  createSlide, createSlideSet, deleteSlide, listSetSlides, listSlideSets, readImageFile,
  updateSlide, updateSlideSet,
  type NoticeSlideDto, type NoticeSlideSetDto, type SlideImageInput, type SlideKind,
} from '../../api/notice-slides';
import { NewGroupModal } from './NewGroupModal';
import './NoticeSlide.css';

/**
 * 통합설정 › 대시보드 › **공지 슬라이드** (2026-09-08 · migration 172 · 시안 화면 J).
 *
 * 대시보드 「공지사항」 위젯이 도는 것을 회사 관리자가 여기서 정한다.
 *
 * ★★★ **기한은 비울 수 없다.** 끝난 공지가 영영 돌면 사람들은 위젯 자체를 안 보게 된다.
 *     그래서 기간이 곧 게시 여부이고, 「상태」 라는 칸을 따로 두지 않는다 — 두면 둘이 어긋난다.
 *     아래 「상태」 배지는 **저장된 값이 아니라 기간에서 계산한 것**이다.
 *
 * ★★ 순서는 위에서부터 위젯의 차례다(정본 E1-3) — 「순서변경」으로 들어가 위·아래로 옮긴다.
 *
 * ★ 정본 부품으로 짠다: A8 ListPane · E1 Table · E5 Tabs · F1 Modal · B7 Switch · D14 Chip.
 *   **B10 드롭존 · B12 색 고르기는 규격만 있고 공용 부품이 없어** 이 화면 안에만 두었다 —
 *   두 번째 쓰는 자리가 생기면 그때 공용으로 올린다(지금 올리면 쓰는 곳이 하나다).
 */

const TAG_COLORS = [
  { value: 'blue', label: '파랑' },
  { value: 'orange', label: '주황' },
  { value: 'green', label: '초록' },
  { value: 'purple', label: '보라' },
  { value: 'teal', label: '청록' },
  { value: 'pink', label: '분홍' },
] as const;

const IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/** 기간에서 지금 상태를 «계산» 한다 — 저장된 상태 칸은 없다. */
function phaseOf(s: NoticeSlideDto, now = Date.now()): { label: string; variant: 'success' | 'warning' | 'default' } {
  const from = new Date(s.startsAt).getTime();
  const to = new Date(s.endsAt).getTime();
  if (now < from) return { label: '게시 예정', variant: 'warning' };
  if (now >= to) return { label: '게시 끝', variant: 'default' };
  return { label: '게시 중', variant: 'success' };
}

const ymd = (iso: string) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
/** 끝나는 날은 **그날 끝까지** 살린다 — 서버 경계가 [시작, 끝) 이라 자정으로 보내면 하루가 통째로 빠진다. */
const endOfDay = (d: string) => new Date(`${d}T23:59:59`).toISOString();
const startOfDay = (d: string) => new Date(`${d}T00:00:00`).toISOString();

interface DraftState {
  kind: SlideKind;
  title: string;
  summary: string;
  tagLabel: string;
  tagColor: string;
  linkUrl: string;
  from: string;
  to: string;
  scrim: boolean;
  image: SlideImageInput | null;
  imagePreview: string | null;
  imageName: string;
}

function emptyDraft(kind: SlideKind): DraftState {
  const today = new Date();
  const twoWeeks = new Date(today.getTime() + 14 * 86_400_000);
  return {
    kind, title: '', summary: '', tagLabel: '', tagColor: 'blue', linkUrl: '',
    from: today.toISOString().slice(0, 10), to: twoWeeks.toISOString().slice(0, 10),
    scrim: true, image: null, imagePreview: null, imageName: '',
  };
}

export function NoticeSlideSection() {
  const [sets, setSets] = useState<NoticeSlideSetDto[]>([]);
  const [setId, setSetId] = useState<string | null>(null);
  const [slides, setSlides] = useState<NoticeSlideDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ordering, setOrdering] = useState(false);

  const [open, setOpen] = useState(false);
  /** 「묶음 만들기」 창 (2026-09-09) — 종전에는 `window.prompt` 였다. */
  const [setOpenNew, setSetOpenNew] = useState(false);
  const [newSetError, setNewSetError] = useState<string | null>(null);
  const [editing, setEditing] = useState<NoticeSlideDto | null>(null);
  const [draft, setDraft] = useState<DraftState>(() => emptyDraft('IMAGE'));
  const [formError, setFormError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const cur = useMemo(() => sets.find((s) => s.id === setId) ?? null, [sets, setId]);

  const loadSets = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listSlideSets();
      setSets(rows);
      setSetId((prev) => prev ?? rows[0]?.id ?? null);
      setError(null);
    } catch {
      setError('묶음을 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadSets(); }, [loadSets]);

  useEffect(() => {
    if (!setId) { setSlides([]); return; }
    let alive = true;
    void listSetSlides(setId)
      .then((rows) => { if (alive) setSlides(rows); })
      .catch(() => { if (alive) setError('슬라이드를 불러오지 못했습니다'); });
    return () => { alive = false; };
  }, [setId]);

  const reloadSlides = useCallback(async () => {
    if (!setId) return;
    setSlides(await listSetSlides(setId));
    setSets(await listSlideSets());
  }, [setId]);

  /* ── 묶음 ─────────────────────────────────────────────────────── */
  /**
   * ★★★ 종전에는 `window.prompt('새 묶음 이름')` 이었다 — 테마도 글꼴도 안 따라가고,
   *     **이름 말고는 아무것도 못 정했다**(기본 묶음으로 할지도 못 골랐다).
   */
  const addSet = async (name: string, isDefault: boolean) => {
    setBusy(true);
    setNewSetError(null);
    try {
      const created = await createSlideSet(name, isDefault);
      await loadSets();
      setSetId(created.id);
      setSetOpenNew(false);
    } catch {
      setNewSetError('묶음을 만들지 못했습니다');
    } finally { setBusy(false); }
  };

  const openNewSet = () => { setNewSetError(null); setSetOpenNew(true); };

  const patchSet = async (patch: Parameters<typeof updateSlideSet>[1]) => {
    if (!cur) return;
    setBusy(true);
    try {
      const next = await updateSlideSet(cur.id, patch);
      setSets((rows) => rows.map((r) => (r.id === next.id ? { ...r, ...next } : r)));
    } catch {
      setError('표시 규칙을 저장하지 못했습니다');
    } finally { setBusy(false); }
  };

  /* ── 슬라이드 ─────────────────────────────────────────────────── */
  const move = async (index: number, dir: -1 | 1) => {
    const next = [...slides];
    const to = index + dir;
    if (to < 0 || to >= next.length) return;
    [next[index]!, next[to]!] = [next[to]!, next[index]!];
    setSlides(next);           // 눈에 먼저 반영 — 저장은 뒤따른다.
    setBusy(true);
    try {
      await Promise.all(next.map((s, i) => (s.sortOrder === i ? null : updateSlide(s.id, { sortOrder: i }))));
      await reloadSlides();
    } catch {
      setError('순서를 저장하지 못했습니다');
      await reloadSlides();
    } finally { setBusy(false); }
  };

  const remove = async (s: NoticeSlideDto) => {
    if (!window.confirm(`「${s.title}」 슬라이드를 지울까요? 되돌릴 수 없습니다.`)) return;
    setBusy(true);
    try {
      await deleteSlide(s.id);
      await reloadSlides();
    } catch {
      setError('지우지 못했습니다');
    } finally { setBusy(false); }
  };

  const openNew = (kind: SlideKind) => {
    setEditing(null);
    setDraft(emptyDraft(kind));
    setFormError(null);
    setOpen(true);
  };

  const openEdit = (s: NoticeSlideDto) => {
    setEditing(s);
    setDraft({
      kind: s.kind, title: s.title, summary: s.summary ?? '',
      tagLabel: s.tagLabel ?? '', tagColor: s.tagColor ?? 'blue', linkUrl: s.linkUrl ?? '',
      from: ymd(s.startsAt), to: ymd(s.endsAt), scrim: s.scrim,
      image: null, imagePreview: s.imageUrl, imageName: '',
    });
    setFormError(null);
    setOpen(true);
  };

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > IMAGE_MAX_BYTES) {
      setFormError('이미지는 2MB 이하만 올릴 수 있습니다');
      return;
    }
    try {
      const image = await readImageFile(file);
      setDraft((d) => ({
        ...d, image, imageName: file.name,
        imagePreview: `data:${image.contentType};base64,${image.data}`,
      }));
      setFormError(null);
    } catch {
      setFormError('이미지를 읽지 못했습니다');
    }
  };

  const save = async () => {
    if (!setId) return;
    if (!draft.title.trim()) { setFormError('제목을 적어 주세요'); return; }
    if (!draft.from || !draft.to) { setFormError('게시 기간을 정해 주세요'); return; }
    if (draft.kind === 'IMAGE' && !draft.image && !editing?.hasImage) {
      setFormError('이미지 슬라이드는 그림이 있어야 합니다');
      return;
    }
    const payload = {
      kind: draft.kind,
      title: draft.title.trim(),
      summary: draft.summary.trim() || null,
      tagLabel: draft.tagLabel.trim() || null,
      tagColor: draft.tagLabel.trim() ? draft.tagColor : null,
      linkUrl: draft.linkUrl.trim() || null,
      scrim: draft.scrim,
      startsAt: startOfDay(draft.from),
      endsAt: endOfDay(draft.to),
      // ★ 종류를 IMAGE→TEXT 로 바꾼 뒤 저장하면 «글형인데 그림이 달린 줄» 이 된다 —
      //   고른 그림이 초안에 남아 있기 때문이다. 종류를 함께 보고 자른다(서버 PATCH 도 같은 것을 한다).
      ...(draft.kind === 'IMAGE' && draft.image ? { image: draft.image } : {}),
    };
    setBusy(true);
    try {
      if (editing) await updateSlide(editing.id, payload);
      else await createSlide(setId, payload);
      setOpen(false);
      await reloadSlides();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(msg ?? '저장하지 못했습니다');
    } finally { setBusy(false); }
  };

  /* ── 표 ───────────────────────────────────────────────────────── */
  const columns = [
    {
      key: 'slide',
      header: '슬라이드',
      rowHeader: true,
      render: (s: NoticeSlideDto) => (
        <span className="nsl-cell">
          {s.kind === 'IMAGE' && s.imageUrl
            ? <img className="nsl-thumb" src={s.imageUrl} alt="" />
            : (
              <span className="nsl-thumb nsl-thumb--text" data-tagcolor={s.tagColor ?? undefined} aria-hidden>
                <i /><i /><i />
              </span>
            )}
          <span className="nsl-cell__meta">
            <b className="nsl-cell__title">{s.title}</b>
            {s.summary && <span className="nsl-cell__sub">{s.summary}</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: '종류',
      width: '110px',
      render: (s: NoticeSlideDto) => (
        <Chip inline size="sm" lead={s.kind === 'IMAGE' ? <ImageIcon size={12} /> : <Megaphone size={12} />}>
          {s.kind === 'IMAGE' ? '이미지' : '텍스트'}
        </Chip>
      ),
    },
    {
      key: 'period',
      header: '게시 기간',
      width: '190px',
      render: (s: NoticeSlideDto) => <span className="nsl-num">{ymd(s.startsAt)} ~ {ymd(s.endsAt)}</span>,
    },
    {
      key: 'phase',
      header: '상태',
      width: '110px',
      render: (s: NoticeSlideDto) => {
        const p = phaseOf(s);
        return <Badge variant={p.variant} size="sm">{p.label}</Badge>;
      },
    },
    ordering
      ? {
        key: 'order',
        header: '순서',
        width: '110px',
        render: (s: NoticeSlideDto, i: number) => (
          <span className="nsl-acts">
            <Button
              variant="secondary" size="sm" iconOnly disabled={i === 0 || busy}
              aria-label={`${s.title} 위로`} onClick={() => { void move(i, -1); }}
            >
              <ChevronUp size={14} aria-hidden />
            </Button>
            <Button
              variant="secondary" size="sm" iconOnly disabled={i === slides.length - 1 || busy}
              aria-label={`${s.title} 아래로`} onClick={() => { void move(i, 1); }}
            >
              <ChevronDown size={14} aria-hidden />
            </Button>
          </span>
        ),
      }
      : {
        key: 'manage',
        header: '관리',
        width: '150px',
        render: (s: NoticeSlideDto) => (
          <span className="nsl-acts">
            <Button variant="secondary" size="sm" iconLeft={<Pencil size={13} />} disabled={busy} onClick={() => openEdit(s)}>
              수정
            </Button>
            <Button variant="secondary" size="sm" iconOnly aria-label={`${s.title} 삭제`} disabled={busy} onClick={() => { void remove(s); }}>
              <Trash2 size={14} aria-hidden />
            </Button>
          </span>
        ),
      },
  ];

  return (
    <section className="svc-cat svc-cat--menu">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Megaphone size={17} /></span>
        <span className="svc-cat__title">공지 슬라이드</span>
        <span className="svc-cat__count">{sets.length}</span>
        <span className="svc-cat__desc">
          대시보드 「공지사항」 위젯이 도는 것 — 이미지 공지와 글 공지가 한 줄로 돌아갑니다
        </span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <div className="nsl">
        <ListPane
          title="슬라이드 묶음"
          count={sets.length}
          ariaLabel="슬라이드 묶음"
          action={<Button variant="primary" size="sm" iconLeft={<Plus size={14} />} onClick={openNewSet}>추가</Button>}
          footer={cur && (
            <dl className="nsl-sum">
              <div><dt>고른 묶음</dt><dd>{cur.name}</dd></div>
              <div><dt>슬라이드</dt><dd>{cur.slideCount ?? slides.length}장</dd></div>
              <div><dt>지금 도는 것</dt><dd>{cur.liveCount ?? 0}장</dd></div>
            </dl>
          )}
        >
          {sets.map((s) => (
            <ListPaneItem
              key={s.id}
              name={s.name}
              selected={s.id === setId}
              badge={s.isDefault ? <Badge variant="success" size="sm">기본</Badge> : undefined}
              onClick={() => { setSetId(s.id); setOrdering(false); }}
            />
          ))}
        </ListPane>

        <div className="nsl-work">
          {!cur && !loading && (
            <EmptyState
              icon={Megaphone}
              title="슬라이드 묶음이 없습니다"
              description="묶음을 하나 만들면 대시보드 위젯이 그 묶음을 돕니다."
              action={<Button variant="primary" iconLeft={<Plus size={14} />} onClick={openNewSet}>묶음 만들기</Button>}
            />
          )}

          {cur && (
            <>
              <div className="nsl-work__head">
                <span className="nsl-work__name">{cur.name}</span>
                {cur.isDefault && <Badge variant="info" size="sm">위젯이 이 묶음을 돕니다</Badge>}
                <span className="nsl-work__sp" />
                <Button variant="secondary" size="sm" disabled={busy || slides.length < 2} onClick={() => setOrdering((v) => !v)}>
                  {ordering ? '순서변경 끝' : '순서변경'}
                </Button>
                <Button variant="secondary" size="sm" iconLeft={<Megaphone size={14} />} disabled={busy} onClick={() => openNew('TEXT')}>
                  텍스트 슬라이드
                </Button>
                <Button variant="primary" size="sm" iconLeft={<Plus size={14} />} disabled={busy} onClick={() => openNew('IMAGE')}>
                  이미지 슬라이드
                </Button>
              </div>

              {slides.length === 0
                ? (
                  <EmptyState
                    icon={ImageIcon}
                    title="슬라이드가 없습니다"
                    description="이미지 한 장이나 글 한 편을 올리면 대시보드에서 돌아갑니다."
                  />
                )
                : (
                  <Table
                    caption={`${cur.name} 슬라이드`}
                    columns={columns}
                    rows={slides}
                    rowKey={(s) => s.id}
                  />
                )}

              <div className="nsl-rules">
                <h4 className="nsl-rules__h">표시 규칙</h4>
                <p className="nsl-rules__hint">
                  위에서부터 돕니다. <b>기한이 지난 슬라이드는 목록에만 남고 위젯에서 저절로 빠집니다.</b>
                </p>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">자동 넘김</span>
                  <Switch
                    checked={cur.autoPlay}
                    onCheckedChange={(v) => { void patchSet({ autoPlay: v }); }}
                    label={cur.autoPlay ? '켬' : '끔'}
                    disabled={busy}
                  />
                  <Select
                    ariaLabel="넘김 간격"
                    value={String(cur.intervalSec)}
                    onChange={(v) => { void patchSet({ intervalSec: Number(v) }); }}
                    options={[4, 6, 8, 10].map((n) => ({ value: String(n), label: `${n}초` }))}
                    size="sm"
                    disabled={busy || !cur.autoPlay}
                  />
                  <span className="nsl-rules__note">
                    글이 긴 슬라이드가 섞이면 <b>6초 아래로 내리지 않는 편</b>이 좋습니다 — 다 읽기 전에 넘어갑니다.
                  </span>
                </div>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">전환</span>
                  <Select
                    ariaLabel="전환 방식"
                    value={cur.transition}
                    onChange={(v) => { void patchSet({ transition: v as 'SLIDE' | 'FADE' }); }}
                    options={[{ value: 'SLIDE', label: '밀기' }, { value: 'FADE', label: '겹쳐 넘기기' }]}
                    size="sm"
                    disabled={busy}
                  />
                  <span className="nsl-rules__note">
                    「움직임 줄이기」를 켠 사람에게는 이 값과 무관하게 겹쳐 넘깁니다.
                  </span>
                </div>

                <div className="nsl-rules__row">
                  <span className="nsl-rules__k">최대 장수</span>
                  <Select
                    ariaLabel="최대 장수"
                    value={String(cur.maxSlides)}
                    onChange={(v) => { void patchSet({ maxSlides: Number(v) }); }}
                    options={[3, 5, 8].map((n) => ({ value: String(n), label: `${n}장` }))}
                    size="sm"
                    disabled={busy}
                  />
                  <span className="nsl-rules__note">넘치면 <b>순서 뒤엣것</b>이 빠집니다 — 목록에서 본 차례가 곧 위젯의 차례입니다.</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {setOpenNew && (
        <NewGroupModal
          title="슬라이드 묶음 만들기"
          namePlaceholder="예: 전사 공지"
          flagLabel="위젯이 이 묶음을 돌게 한다"
          flagHint="켜면 대시보드 「공지 슬라이드」 위젯이 이 묶음을 돕니다. 켤 수 있는 묶음은 하나입니다."
          isFirst={sets.length === 0}
          footNote="자동 넘김 · 간격 · 전환 · 최대 장수는 만든 뒤 오른쪽에서 정합니다."
          busy={busy}
          error={newSetError}
          onClose={() => setSetOpenNew(false)}
          onSubmit={(name, isDefault) => { void addSet(name, isDefault); }}
        />
      )}

      {open && (
        <Modal
          title={editing ? '슬라이드 수정' : '슬라이드 추가'}
          onClose={() => setOpen(false)}
          size="md"
          accent
          footer={(
            <>
              <ModalFootNote>
                {cur ? <>등록하면 <b>{cur.name}</b> 묶음 맨 뒤에 놓입니다</> : null}
              </ModalFootNote>
              <ModalActions>
                <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>취소</Button>
                <Button variant="primary" onClick={() => { void save(); }} loading={busy}>{editing ? '저장' : '등록'}</Button>
              </ModalActions>
            </>
          )}
        >
          <Tabs
            ariaLabel="슬라이드 종류"
            items={[{ value: 'IMAGE', label: '이미지 슬라이드' }, { value: 'TEXT', label: '텍스트 슬라이드' }]}
            value={draft.kind}
            onChange={(v) => setDraft((d) => ({ ...d, kind: v as SlideKind }))}
            variant="pill"
          />

          {formError && <div className="admin-console__alert admin-console__alert--warn" role="alert">{formError}</div>}

          {draft.kind === 'IMAGE' ? (
            <>
              {/* 정본 B10 — 규격만 있고 공용 부품이 없어 이 화면 안에만 둔다. */}
              <div
                className={`nsl-drop${draft.imagePreview ? ' has-file' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); void pickFile(e.dataTransfer.files?.[0]); }}
              >
                {draft.imagePreview
                  ? <img className="nsl-drop__prev" src={draft.imagePreview} alt="고른 이미지 미리보기" />
                  : <ImageIcon size={26} aria-hidden />}
                <b>{draft.imagePreview ? (draft.imageName || '지금 그림') : '이미지를 끌어다 놓으세요'}</b>
                <span>.jpg · .png · .webp · 1600×900 권장 · 최대 2MB</span>
                <Button variant="secondary" size="sm" iconLeft={<Upload size={13} />} onClick={() => fileRef.current?.click()}>
                  파일 고르기
                </Button>
                {draft.imagePreview && (
                  <Button
                    variant="ghost" size="sm" iconLeft={<X size={13} />}
                    onClick={() => setDraft((d) => ({ ...d, image: null, imagePreview: null, imageName: '' }))}
                  >
                    빼기
                  </Button>
                )}
                <input
                  ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                  onChange={(e) => { void pickFile(e.target.files?.[0]); e.target.value = ''; }}
                />
              </div>

              <FormField label="글 뒤를 어둡게" hint="밝은 그림에서 끄면 제목이 그림에 묻힙니다.">
                <Switch checked={draft.scrim} onCheckedChange={(v) => setDraft((d) => ({ ...d, scrim: v }))} label={draft.scrim ? '켬' : '끔'} />
              </FormField>
            </>
          ) : (
            <p className="nsl-note">
              <b>이미지 없이 등록합니다.</b> 그림이 없는 공지에 억지로 배경을 만들면 아무 뜻 없는 그림이
              매일 한 자리를 차지합니다. 텍스트 슬라이드는 <b>태그 색</b>만으로 다른 슬라이드와 구별됩니다.
            </p>
          )}

          <div className="nsl-form2">
            <FormField label="태그" hint="색은 분류를 가릅니다 — 급한 정도를 뜻하지 않습니다.">
              <Input value={draft.tagLabel} maxLength={16} placeholder="총무 · 인사 · 전사"
                onChange={(e) => setDraft((d) => ({ ...d, tagLabel: e.target.value }))} />
            </FormField>
            <FormField label="태그 색">
              {/* 정본 B12 — 정해 둔 색판에서 하나. 공용 부품이 없어 여기 안에만 둔다. */}
              <span className="nsl-colors" role="radiogroup" aria-label="태그 색">
                {TAG_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    role="radio"
                    aria-checked={draft.tagColor === c.value}
                    aria-label={c.label}
                    className={`nsl-color${draft.tagColor === c.value ? ' is-on' : ''}`}
                    data-tagcolor={c.value}
                    onClick={() => setDraft((d) => ({ ...d, tagColor: c.value }))}
                  />
                ))}
              </span>
            </FormField>
          </div>

          <FormField label="제목" required counter={{ value: draft.title.length, max: 120 }}>
            <Input value={draft.title} maxLength={120} fullWidth
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
          </FormField>

          <FormField label="요약" hint="위젯에서 두 줄까지 보이고 넘치면 말줄임 됩니다.">
            <Input value={draft.summary} maxLength={300} fullWidth
              onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))} />
          </FormField>

          <div className="nsl-form2">
            <FormField label="시작" required>
              <Input type="date" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </FormField>
            <FormField label="끝" required hint="끝나는 날 자정까지 돕니다 — 지나면 저절로 빠집니다.">
              <Input type="date" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </FormField>
          </div>

          <FormField label="눌렀을 때 열 곳" hint="비워 두면 눌러도 아무 일이 없습니다(그것이 기본값입니다).">
            <Input value={draft.linkUrl} placeholder="/board/12 또는 https://…" fullWidth
              onChange={(e) => setDraft((d) => ({ ...d, linkUrl: e.target.value }))} />
          </FormField>
        </Modal>
      )}
    </section>
  );
}
